import { FC, useEffect, useMemo, useState } from 'react';

import { PageActivityFeatures } from 'constants/pageActivityFeatures';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import Table from 'modules/_shared/Table';
import TableBody from 'modules/_shared/Table/components/TableBody';
import { TablePagination } from 'modules/_shared/Table/components/TablePagination';
import { useTranslate } from 'hooks/useTranslate';

import AnalyticsEmptyState from '../components/AnalyticsEmptyState';
import AnalyticsFilters from '../components/AnalyticsFilters';
import CustomLandingAnalyticsTableHeader from '../components/CustomLandingAnalyticsTableHeader';
import CustomLandingAnalyticsTableRow from '../components/CustomLandingAnalyticsTableRow';
import { useAnalyticsEvents } from '../hooks/useAnalyticsEvents';
import { formatEventRow } from '../utils/formatEventRow';

type CustomLandingTabProps = {
  societyId?: string;
};

const formatLocalDate = (date: Date): string => date.toISOString().slice(0, 10);

const buildDefaultRange = (): { from: string; to: string } => {
  const today = new Date();
  const thirtyAgo = new Date(today);
  thirtyAgo.setDate(today.getDate() - 30);
  return { from: formatLocalDate(thirtyAgo), to: formatLocalDate(today) };
};

const CustomLandingTab: FC<CustomLandingTabProps> = ({ societyId }) => {
  const { t } = useTranslate();
  const defaults = useMemo(buildDefaultRange, []);
  const [fromDate, setFromDate] = useState<string>(defaults.from);
  const [toDate, setToDate] = useState<string>(defaults.to);
  const [partnerSearch, setPartnerSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paginationRemountKey, setPaginationRemountKey] = useState(0);

  const { events, total, isLoading } = useAnalyticsEvents({
    societyId,
    feature: PageActivityFeatures.CUSTOM_LANDING,
    fromDate,
    toDate,
    page,
    limit: rowsPerPage,
    partnerSearch,
  });

  const rows = useMemo(
    () => events.map((event, index) => formatEventRow(event, index)),
    [events]
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / rowsPerPage)),
    [total, rowsPerPage]
  );

  useEffect(() => {
    if (page > totalPages && total > 0) {
      setPage(totalPages);
      setPaginationRemountKey((k) => k + 1);
    }
  }, [page, totalPages, total]);

  const handleChangeFromDate = (value: string) => {
    setFromDate(value);
    setPage(1);
  };

  const handleChangeToDate = (value: string) => {
    setToDate(value);
    setPage(1);
  };

  const handleChangePartnerSearch = (value: string) => {
    setPartnerSearch(value);
    setPage(1);
  };

  const handleChangePage = (nextPage: number) => {
    setPage(nextPage);
  };

  const handleChangeRowsPerPage = (nextRows: number) => {
    setRowsPerPage(nextRows);
    setPage(1);
  };

  const tableContent = (
    <>
      <CustomLandingAnalyticsTableHeader />
      <TableBody
        hasData={rows.length > 0}
        content={rows.map((row) => (
          <CustomLandingAnalyticsTableRow key={row.id} row={row} />
        ))}
        noDataMessage={t('NoActivityFound')}
        noDataColspan={8}
      />
    </>
  );

  const paginationKey = `${fromDate}-${toDate}-${partnerSearch}-${rowsPerPage}-${paginationRemountKey}`;

  return (
    <div className="nk-block pt-0" data-testid="analytics-custom-landing-tab">
      <AnalyticsFilters
        fromDate={fromDate}
        toDate={toDate}
        partnerSearch={partnerSearch}
        onChangeFromDate={handleChangeFromDate}
        onChangeToDate={handleChangeToDate}
        onChangePartnerSearch={handleChangePartnerSearch}
      />

      {total > 0 ? (
        <>
          <Table content={tableContent} scrollHorizontal />
          <TablePagination
            key={paginationKey}
            totalRows={total}
            defaultPage={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            defaultRowsPerPage={rowsPerPage}
          />
        </>
      ) : (
        !isLoading && <AnalyticsEmptyState />
      )}

      {isLoading && <CustomLoading />}
    </div>
  );
};

export default CustomLandingTab;
